import React, { useEffect, useRef, useState } from 'react';
import { useGoogleMapsContext } from '../providers/GoogleMapsProvider';
import './GoogleMapPicker.css';

/**
 * Interactive Google Map with draggable marker.
 * Allows user to select precise location by dragging marker or clicking on map.
 *
 * Part of Store Location Editor feature (STORE_LOCATION_EDITOR_ENTERPRISE_PLAN).
 *
 * @param {Object} props
 * @param {number} props.latitude - Initial latitude (required)
 * @param {number} props.longitude - Initial longitude (required)
 * @param {boolean} props.showMarker - Whether to show marker (default: true)
 * @param {function} props.onMapClick - Called when user clicks on map, emits {latitude, longitude}
 * @param {function} props.onMarkerDragEnd - Called when marker drag ends, emits {latitude, longitude}
 * @param {number} props.zoom - Map zoom level (default: 16)
 * @param {string} props.height - Map container height (default: '400px')
 * @param {string} props.className - Additional CSS classes
 *
 * @example
 * <GoogleMapPicker
 *   latitude={17.421268}
 *   longitude={78.588932}
 *   showMarker={true}
 *   onMapClick={({ latitude, longitude }) => {
 *     console.log('Clicked:', latitude, longitude);
 *   }}
 *   onMarkerDragEnd={({ latitude, longitude }) => {
 *     console.log('Dragged:', latitude, longitude);
 *   }}
 *   zoom={16}
 *   height="500px"
 * />
 */
const GoogleMapPicker = ({
  latitude,
  longitude,
  showMarker = true,
  onMapClick,
  onMarkerDragEnd,
  zoom = 16,
  height = '400px',
  className = ''
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);
  const onMapClickRef = useRef(onMapClick);
  const onMarkerDragEndRef = useRef(onMarkerDragEnd);
  const { isLoaded, loadError } = useGoogleMapsContext();
  const [mapError, setMapError] = useState(null);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    onMarkerDragEndRef.current = onMarkerDragEnd;
  }, [onMarkerDragEnd]);

  useEffect(() => {
    // Wait for Google Maps to load
    if (!isLoaded || !window.google?.maps || !mapContainerRef.current) {
      return;
    }

    // Validate coordinates
    if (latitude == null || longitude == null || isNaN(latitude) || isNaN(longitude)) {
      setMapError('Invalid coordinates provided');
      return;
    }

    try {
      // Initialize map with dark theme
      const mapOptions = {
        center: { lat: Number(latitude), lng: Number(longitude) },
        zoom: Number(zoom),
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        gestureHandling: 'cooperative', // Require Ctrl/Cmd to zoom, prevents scroll hijacking
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#212121' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          {
            featureType: 'administrative.locality',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ color: '#263c3f' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#6b9a76' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#38414e' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#212a37' }]
          },
          {
            featureType: 'road',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9ca5b3' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry',
            stylers: [{ color: '#746855' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#1f2835' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#f3d19c' }]
          },
          {
            featureType: 'transit',
            elementType: 'geometry',
            stylers: [{ color: '#2f3948' }]
          },
          {
            featureType: 'transit.station',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#17263c' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#515c6d' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#17263c' }]
          }
        ]
      };

      const map = new window.google.maps.Map(mapContainerRef.current, mapOptions);
      mapInstanceRef.current = map;

      // Add click listener to map (for placing marker when it doesn't exist)
      map.addListener('click', (event) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();
        onMapClickRef.current?.({ latitude: newLat, longitude: newLng });
      });

      // Create draggable marker (only if showMarker is true)
      if (showMarker) {
        const marker = new window.google.maps.Marker({
          position: { lat: Number(latitude), lng: Number(longitude) },
          map: map,
          draggable: true,
          title: 'Store Location (drag to adjust)',
          animation: window.google.maps.Animation.DROP
        });

        markerInstanceRef.current = marker;

        // Listen for marker drag end
        marker.addListener('dragend', (event) => {
          const newLat = event.latLng.lat();
          const newLng = event.latLng.lng();
          onMarkerDragEndRef.current?.({ latitude: newLat, longitude: newLng });
        });
      }

    } catch (error) {
      setMapError('Failed to initialize map');
    }

    // Cleanup
    return () => {
      if (markerInstanceRef.current) {
        markerInstanceRef.current.setMap(null);
        markerInstanceRef.current = null;
      }
      if (mapInstanceRef.current) {
        window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
        mapInstanceRef.current = null;
      }
    };
  }, [isLoaded, latitude, longitude, zoom, showMarker]);

  // Update marker position when coordinates change externally (only if marker exists)
  useEffect(() => {
    if (!showMarker || !markerInstanceRef.current || !mapInstanceRef.current) {
      return;
    }

    const newPosition = { lat: Number(latitude), lng: Number(longitude) };
    markerInstanceRef.current.setPosition(newPosition);
    mapInstanceRef.current.panTo(newPosition);
  }, [latitude, longitude, showMarker]);

  // Show error state
  if (loadError || mapError) {
    return (
      <div
        className={`google-map-picker-error ${className}`}
        style={{ height }}
      >
        <div className="error-content">
          <p className="error-message">
            {loadError || mapError}
          </p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (!isLoaded) {
    return (
      <div
        className={`google-map-picker-loading ${className}`}
        style={{ height }}
      >
        <div className="loading-content">
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mapContainerRef}
      className={`google-map-picker ${className}`}
      style={{ height }}
    />
  );
};

export default GoogleMapPicker;
